import { useCallback, useEffect, useRef, useState } from "react";
import { startLearningLlmStream } from "../services/learningGraphqlSseClient";
import { AttachmentInput } from "../graphql/generated/graphql";

type StreamCompletionEvent = { chatId?: string; messageId?: string };

export type StreamAudioChunkPayload = {
  audioBase64: string;
  text?: string;
  seq: number;
  messageId?: string;
  isFinalChunk?: boolean;
};

export type AudioSegmentStartPayload = {
  messageId?: string;
  chunkIndex: number;
};

export type AudioSegmentTextPayload = {
  messageId?: string;
  chunkIndex: number;
  text: string;
};

export type AudioSegmentEndPayload = {
  messageId?: string;
  chunkIndex: number;
};

interface StartStreamOptions {
  chatId?: string;
  workspaceId?: string;
  attachment?: AttachmentInput;
  contentNodeId?: string;
  enableTts?: boolean;
  onChunk?: (payload: { text: string; messageId?: string; isFinalChunk?: boolean }) => void;
  onAudioChunk?: (payload: StreamAudioChunkPayload) => void;
  onAudioSegmentStart?: (payload: AudioSegmentStartPayload) => void;
  onAudioSegmentText?: (payload: AudioSegmentTextPayload) => void;
  onAudioSegmentEnd?: (payload: AudioSegmentEndPayload) => void;
  onChatId?: (chatId: string) => void;
  onComplete?: (payload: { event?: StreamCompletionEvent; text: string }) => void;
  onObjectiveCompleted?: (payload: { objectiveIds: string[] }) => void;
  onAllObjectivesCompleted?: () => void;
  onError?: (payload: { error: Error }) => void;
}

export function useLearningLlmStream() {
  const controllerRef = useRef<AbortController | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const startStream = useCallback(async (question: string, options: StartStreamOptions = {}) => {
    const {
      onComplete,
      onError,
      onChunk,
      onAudioChunk,
      onAudioSegmentStart,
      onAudioSegmentText,
      onAudioSegmentEnd,
      onChatId,
      onObjectiveCompleted,
      onAllObjectivesCompleted,
      ...streamOptions
    } = options;

    if (!streamOptions.contentNodeId) {
      throw new Error("Missing contentNodeId for learning LLM stream.");
    }

    controllerRef.current?.abort();

    const controller = new AbortController();
    controllerRef.current = controller;

    setIsStreaming(true);

    let accumulatedText = "";
    let completionEvent: StreamCompletionEvent | undefined;

    try {
      await startLearningLlmStream(
        question,
        controller,
        {
          onChunk: (event) => {
            const ev = event as Record<string, unknown>;
            if (ev.__typename === "LLMStreamMessageDeltaEvent") {
              if (ev.chatId) {
                onChatId?.(ev.chatId as string);
              }
              const delta = ev.delta as { __typename?: string; text?: string } | undefined;
              if (delta?.__typename === "LLMStreamTextBlock") {
                const text = delta.text ?? "";
                if (text) {
                  accumulatedText += text;
                }
                onChunk?.({
                  text,
                  messageId: ev.messageId as string | undefined,
                  isFinalChunk: (ev.isFinalChunk as boolean | undefined) ?? false,
                });
                if (ev.isFinalChunk) {
                  setIsStreaming(false);
                }
              }
            }

            if (ev.__typename === "LLMStreamAudioSegmentStartEvent" && typeof ev.chunkIndex === "number") {
              onAudioSegmentStart?.({
                messageId: ev.messageId as string | undefined,
                chunkIndex: ev.chunkIndex,
              });
            }

            if (ev.__typename === "LLMStreamAudioSegmentTextEvent" && typeof ev.chunkIndex === "number") {
              onAudioSegmentText?.({
                messageId: ev.messageId as string | undefined,
                chunkIndex: ev.chunkIndex,
                text: (ev.text as string) ?? "",
              });
            }

            if (ev.__typename === "LLMStreamAudioSegmentEndEvent" && typeof ev.chunkIndex === "number") {
              onAudioSegmentEnd?.({
                messageId: ev.messageId as string | undefined,
                chunkIndex: ev.chunkIndex,
              });
            }

            if (ev.__typename === "LLMStreamMessageCompletedEvent") {
              const msg = ev.message as { chatId?: string; id?: string } | undefined;
              if (msg?.chatId) {
                onChatId?.(msg.chatId);
              }
              completionEvent = {
                chatId: msg?.chatId,
                messageId: msg?.id,
              };
              setIsStreaming(false);
              onComplete?.({ event: completionEvent, text: accumulatedText });
            }

            if (ev.__typename === "LLMStreamArtifactCreatedEvent") {
              const artifact = ev.artifact as
                | {
                    type?: string;
                    data?: { isCompleted?: boolean; objectiveId?: string };
                  }
                | undefined;
              if (
                artifact?.type === "objective.progress.changed" &&
                artifact?.data?.isCompleted === true &&
                typeof artifact?.data?.objectiveId === "string"
              ) {
                onObjectiveCompleted?.({ objectiveIds: [artifact.data.objectiveId] });
              }

              if (artifact?.type === "objectives.completed.all") {
                onAllObjectivesCompleted?.();
              }
            }

            if (ev.__typename === "LLMStreamErrorEvent") {
              const message = (ev.errorMessage as string) || "LLM stream error";
              setIsStreaming(false);
              onError?.({ error: new Error(message) });
            }
          },
          onError: (error) => {
            setIsStreaming(false);
            onError?.({ error });
          },
          onComplete: () => {
            setIsStreaming(false);
          },
        },
        streamOptions
      );
    } catch (err) {
      if ((err as DOMException)?.name === "AbortError") return;
      setIsStreaming(false);
      throw err;
    }
  }, []);

  const cancelStream = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setIsStreaming(false);
  }, []);

  return { startStream, cancelStream, isStreaming };
}

export default useLearningLlmStream;
